import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { clientService } from '../../../services/clientService';
import { clientPathLabel, indexClients } from '../../../utils/clientTree';
import { typesService } from '../../../services/typesService';
import MultiSelectFilter, { MultiSelectOption } from '../../common/MultiSelectFilter';
import config from '../../../config/env';
import { safeStorage } from '../../../utils/safeStorage';
import { DevicesListState } from './useDevicesList';

interface DevicesFiltersProps {
  list: DevicesListState;
  exportControl?: { canExport: boolean; exporting: boolean; notice: string | null; onExport: () => void };
}

/** The template filter options come from the templates API, searched server-side. */
const fetchTemplateOptions = async (search: string, includeInactive: boolean): Promise<MultiSelectOption[]> => {
  const token = safeStorage.getItem('access_token');
  const params = new URLSearchParams({ page: '1', limit: '50' });
  if (search.trim()) params.set('search', search.trim());
  if (includeInactive) params.set('include_inactive', 'true');
  const resp = await fetch(`${config.api.baseUrl}/v2/config-templates?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!resp.ok) return [];
  const data = await resp.json();
  return (data?.items || [])
    .map((tmpl: any) => ({ id: String(tmpl.id), name: String(tmpl.name) }))
    .filter((o: MultiSelectOption) => o.id && o.name);
};

const fetchTemplateLabels = async (ids: string[]): Promise<Record<string, string>> => {
  const out: Record<string, string> = {};
  const token = safeStorage.getItem('access_token');
  await Promise.all(
    ids.filter((i) => i !== '0').map(async (id) => {
      try {
        const r = await fetch(`${config.api.baseUrl}/v2/config-templates/${id}`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (r.ok) {
          const tmpl = await r.json();
          if (tmpl?.name) out[id] = String(tmpl.name);
        }
      } catch {
        /* leave id as label */
      }
    }),
  );
  return out;
};

/** The search box, the four filters, the inactive switch, the export button and the match count. */
export const DevicesFilters: React.FC<DevicesFiltersProps> = ({ list, exportControl }) => {
  const { t } = useI18n();
  const includeInactive = list.showInactive && list.canSeeInactive;

  return (
    <div className="devices-table-header">
      <div className="table-controls">
        <div className="search-container">
          <input
            type="text"
            data-testid="field-devices-search"
            placeholder={t('devices.searchPlaceholder')}
            value={list.searchTerm}
            onChange={(e) => { list.setSearchTerm(e.target.value); list.setCurrentPage(1); }}
            className="search-input search-input--ota-style"
            aria-label={t('devices.searchPlaceholder')}
          />
        </div>

        <div className="filter-controls">
          {list.worksAcrossClients && (
            <MultiSelectFilter
              value={list.filterClient}
              onChange={(next) => { list.setFilterClient(next); list.setCurrentPage(1); }}
              fetchOptions={async (search, includeInactiveOpt) => {
                const clients = includeInactiveOpt ? await clientService.fetchAllClients() : await clientService.fetchActiveClients();
                const byId = indexClients(await clientService.fetchAllClients());
                const needle = search.toLowerCase();
                return clients
                  .map((c) => ({ id: c.id, name: clientPathLabel(c, byId) }))
                  .filter((c) => !needle || c.name.toLowerCase().includes(needle))
                  .slice(0, 50);
              }}
              fetchSelectedLabels={async (ids) => {
                const out: Record<string, string> = {};
                for (const id of ids) {
                  const found = list.clientList.find((c) => c.id === id);
                  if (found) out[id] = found.name;
                }
                return out;
              }}
              allLabel={t('devices.allClients')}
              ariaLabel="Filter by client"
              className="filter-client"
              testId="filter-client"
              searchPlaceholder={t('devices.allClients')}
              includeInactive={includeInactive}
            />
          )}

          <MultiSelectFilter
            value={list.filterDeviceType}
            onChange={(next) => { list.setFilterDeviceType(next); list.setCurrentPage(1); }}
            fetchOptions={async (search, includeInactiveOpt) => {
              const types = await typesService.getHardwareTypes(includeInactiveOpt);
              const needle = search.toLowerCase();
              return types
                .filter((ht) => !needle || ht.code.toLowerCase().includes(needle) || (ht.name || '').toLowerCase().includes(needle))
                .slice(0, 50)
                .map((ht) => ({ id: ht.code, name: ht.name ? `${ht.name} (${ht.code})` : ht.code }));
            }}
            fetchSelectedLabels={async (codes) => {
              const out: Record<string, string> = {};
              try {
                const types = await typesService.getHardwareTypes(includeInactive);
                for (const code of codes) {
                  const found = types.find((ht) => ht.code === code);
                  out[code] = found ? (found.name ? `${found.name} (${found.code})` : found.code) : code;
                }
              } catch {
                /* fall back to id */
              }
              return out;
            }}
            allLabel="All Device Types"
            ariaLabel="Filter by type"
            testId="filter-device-type"
            searchPlaceholder="All Device Types"
            includeInactive={includeInactive}
          />

          <MultiSelectFilter
            value={list.filterTemplate}
            onChange={(next) => { list.setFilterTemplate(next); list.setCurrentPage(1); }}
            fetchOptions={fetchTemplateOptions}
            fetchSelectedLabels={fetchTemplateLabels}
            allLabel={t('configTemplates.allTemplates')}
            ariaLabel="Filter by template"
            extraOptions={[{ id: '0', label: t('devices.noTemplate') }]}
            className="filter-template"
            testId="filter-template"
            searchPlaceholder={t('configTemplates.searchPlaceholder')}
            includeInactive={includeInactive}
          />

          <select value={list.filterStatus} onChange={(e) => { list.setFilterStatus(e.target.value); list.setCurrentPage(1); }} className="filter-select">
            <option value="">{t('common.allStatus')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="refresh">{t('devices.needsRefresh')}</option>
          </select>

          {list.canSeeInactive && (
            <label className="filter-toggle-inactive" title={t('common.showInactiveHint')}>
              <input type="checkbox" checked={list.showInactive} onChange={(e) => { list.setShowInactive(e.target.checked); list.setCurrentPage(1); }} />
              {t('common.showInactive')}
            </label>
          )}

          {exportControl?.canExport && (
            <button
              type="button"
              className="devices-export-btn"
              data-testid="btn-devices-export-excel"
              onClick={exportControl.onExport}
              disabled={exportControl.exporting}
              title={t('devices.export.hint')}
            >
              {exportControl.exporting ? t('devices.export.exporting') : t('devices.export.button')}
            </button>
          )}
        </div>

        <div className="table-info">
          {exportControl?.notice && <span className="devices-export-notice" role="status" data-testid="devices-export-notice">{exportControl.notice} </span>}
          {/* The full match count across pages, not the page length. */}
          {t('devices.devicesFound', { count: list.totalItems })}
        </div>
      </div>
    </div>
  );
};
